<script>
  // Material Design 3 outlined text field.
  // - 56px height, 16px text, 4px corner radius (per M3 spec)
  // - Floating label that translates up when focused or filled
  // - Outline thickens to 2px in primary color on focus
  // - Label "notch" effect via solid background on the label that matches
  //   the input's own surface (so it cuts through the border)
  let {
    label,
    value = $bindable(''),
    type = 'text',
    leadingIcon = null,
    trailingIcon = null,
    onchange = null,
    ...rest
  } = $props();

  // Generated input id for label association.
  const id = `tf-${Math.random().toString(36).slice(2, 9)}`;
</script>

<label class="md-tf" for={id}>
  {#if leadingIcon}<span class="material-symbols-outlined md-tf-icon md-tf-leading">{leadingIcon}</span>{/if}
  <input
    {id}
    {type}
    bind:value
    placeholder=" "
    {onchange}
    {...rest}
    class:has-leading={leadingIcon}
    class:has-trailing={trailingIcon}
  />
  <span class="md-tf-label">{label}</span>
  {#if trailingIcon}<span class="material-symbols-outlined md-tf-icon md-tf-trailing">{trailingIcon}</span>{/if}
</label>

<style>
  .md-tf {
    position: relative;
    display: block;
    width: 100%;
  }
  .md-tf input {
    width: 100%;
    height: 56px;
    padding: 0 16px;
    /* Fill matches the surface the field sits on (set by the container via
       --md-tf-surface), so an outlined field on a tinted card reads as a
       clean outline, not a white box. */
    background: rgb(var(--md-tf-surface, var(--md-sys-color-surface)));
    border: 1px solid rgb(var(--md-sys-color-outline));
    border-radius: 4px;
    color: rgb(var(--md-sys-color-on-surface));
    font-size: 16px;
    line-height: 24px;
    outline: none;
    transition: border-color 150ms, border-width 150ms;
    font-family: inherit;
  }
  /* M3 dense variant on phones to recover vertical space. */
  @media (max-width: 768px) {
    .md-tf input { height: 48px; font-size: 14px; }
  }
  .md-tf input::placeholder {
    color: transparent;
  }
  .md-tf input.has-leading  { padding-left: 48px; }
  .md-tf input.has-trailing { padding-right: 48px; }

  .md-tf input:hover {
    border-color: rgb(var(--md-sys-color-on-surface));
  }
  .md-tf input:focus {
    border-color: rgb(var(--md-sys-color-primary));
    border-width: 2px;
    padding: 0 15px;
  }
  .md-tf input:focus.has-leading  { padding-left: 47px; }
  .md-tf input:focus.has-trailing { padding-right: 47px; }

  .md-tf-label {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    padding: 0 4px;
    color: rgb(var(--md-sys-color-on-surface-variant));
    background: rgb(var(--md-tf-surface, var(--md-sys-color-surface)));
    font-size: 16px;
    line-height: 1;
    pointer-events: none;
    transition: top 150ms, font-size 150ms, color 150ms;
    /* Never let a long label spill past the field outline -- clamp to the
       field width and ellipsize. */
    max-inline-size: calc(100% - 16px);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .md-tf input.has-leading ~ .md-tf-label {
    left: 44px;
  }

  /* Floated state: focused OR has content */
  .md-tf input:focus ~ .md-tf-label,
  .md-tf input:not(:placeholder-shown) ~ .md-tf-label {
    top: 0;
    font-size: 12px;
    /* When floated, label sits at the border line regardless of leading icon. */
    left: 12px;
  }
  .md-tf input:focus ~ .md-tf-label {
    color: rgb(var(--md-sys-color-primary));
  }

  .md-tf-icon {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    color: rgb(var(--md-sys-color-on-surface-variant));
    pointer-events: none;
    font-size: 24px;
  }
  .md-tf-leading  { left: 16px; }
  .md-tf-trailing { right: 16px; }
</style>
